import { autoInjectable, container } from 'tsyringe';
import { EpStore } from '../services/store/ep-store';
import { ofType } from 'deox';
import { OverlayActions, RtpsActions } from '../actions';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { CommonEffect } from './common-effect';
import { PublicEventEmitterService } from '../services/public-event-emitter/public-event-emitter.service';
import { Analytics } from '../modules/analytics/analytics';
import { merge, of } from 'rxjs';
import {
  fetchPrescreen,
  offerResponse,
  OfferResponseType,
  pathForRtps,
  PrescreenResult,
  fetchVirtualLookup,
  pathForBps, RtpsMockOptions, VirtualLookupResult, VirtualLookupBody,
} from 'fmc-sdk';
import { fromPromise } from 'rxjs/internal-compatibility';
import { displayPrescreenApprovedOffer } from '../actions/rtps.actions';
import { ConfigUtilService } from '../services/config/config-util.service';
import {
  FetchRtpsMockOptions, getCleanPrescreenResult,
  getPreScreenBodyFromCustomerInfo, getRtpsMockOptions,
  RTPS_OVERLAY_CONTENT_ID,
  VirtualLookupMockOptions
} from '../models/rtps.model';
import { OverlayContentType } from '../models/overlay.model';
import { OverlayService } from '../services/overlay/overlay.service';
import { EpAdServerClient } from '../services/adserver-client/ep-adserver-client';
import { GeneratePlacementRequest } from '../services/adserver-client/api/GeneratePlacementRequest';
import { assignDefined } from '../models/util.model';

@autoInjectable()
export class RtpsEffects implements CommonEffect {

  brandSubmitRTPS$ = this.store.actions$.pipe(
    ofType(RtpsActions.brandSubmitRTPS),
    map(action => action.payload),
    tap(({ customerInfo, mockResponse }) => {
        /**
         * Use stored attribution is available
         * */
        const attributionParams = this.analytics.getSessionAttribution();
        customerInfo = assignDefined({}, customerInfo, attributionParams);

        if (customerInfo.customerAcceptedOffer) {
          this.store.dispatch(RtpsActions.displayBpsApprovalOffer({}, customerInfo, mockResponse as VirtualLookupMockOptions));
        } else if (customerInfo.prescreenId) {
          this.store.dispatch(RtpsActions.virtualLookupReq(customerInfo, mockResponse as VirtualLookupMockOptions));
        } else {
          this.store.dispatch(RtpsActions.fetchPrescreen(customerInfo, mockResponse as FetchRtpsMockOptions));
        }
      },
    ),
    catchError(err => {
      return of();
    }),
  );

  fetchPrescreen$ = this.store.actions$.pipe(
    ofType(RtpsActions.fetchPrescreen),
    map(action => action.payload),
    exhaustMap(({ customerInfo, mockResponse }) => {
        const config = this.configUtilService.getConfig();
        const urlPath = window.location.pathname || '/';
        const preScreenBody = getPreScreenBodyFromCustomerInfo(customerInfo, {}, urlPath);
        return fromPromise(fetchPrescreen(
          preScreenBody,
          {
            clientKey: config.apiKey,
            baseUrl: config.fmcApiBaseUrl,
            mockResponse,
          },
        )).pipe(
          map(preScreenResp => preScreenResp.response),
          tap(preScreen =>
            this.store.dispatch(RtpsActions.submitRtpsSuccess(preScreen, customerInfo, mockResponse)),
          ),
          catchError(err => {
            // this.logger.(err);
            return of();
          }),
          // TODO Catch error
        );
      },
    ),
    catchError(err => {
      // console.log(err);
      return of();
    }),
  );

  virtualLookupReq$ = this.store.actions$.pipe(
    ofType(RtpsActions.virtualLookupReq),
    map(action => action.payload),
    exhaustMap(({ customerInfo, mockResponse }) => {
        const config = this.configUtilService.getConfig();
        const prescreenId = customerInfo.prescreenId;
        const virtualLookupBody: VirtualLookupBody = {
          prescreenId,
          subchannel: customerInfo?.subchannel,
          channel: customerInfo?.channel,
        };

        return fromPromise(fetchVirtualLookup(
          virtualLookupBody,
          {
            clientKey: config.apiKey,
            baseUrl: config.fmcApiBaseUrl,
            mockResponse,
          },
        )).pipe(
          map(virtualLookupResp => virtualLookupResp.response),
          tap(preScreen =>
            this.store.dispatch(RtpsActions.submitVirtualLookupSuccess(preScreen, customerInfo, mockResponse)),
          ),
          catchError(err => {
            // this.logger.(err);
            return of();
          }),
          // TODO Catch error
        );
      },
    ),
    catchError(err => {
      // console.log(err);
      return of();
    }),
  );

  submitRtpsSuccess$ = this.store.actions$.pipe(
    ofType(RtpsActions.submitRtpsSuccess),
    map(action => action.payload),
    tap(({ prescreen, customerInfo, mockResponse }) => {
      switch (prescreen.result) {
        // Has been pre-approved
        case PrescreenResult.Approved:
          this.store.dispatch(RtpsActions.displayPrescreenApprovedOffer(prescreen, customerInfo, mockResponse));

          break;

        // Not pre-approved but should / could apply
        case PrescreenResult.MakeOffer:
        case PrescreenResult.Acknowledge:
          break;
        // User is already a cardholder, suggest them to use their card
        case PrescreenResult.AccountExists:
          this.store.dispatch(RtpsActions.displayTenderRebuttal(prescreen));
          break;
      }
    }),
  );

  submitVirtualLookupSuccess$ = this.store.actions$.pipe(
    ofType(RtpsActions.submitVirtualLookupSuccess),
    map(action => action.payload),
    tap(({ virtualLookup, customerInfo, mockResponse }) => {
      /*
      * Decision based on result.
      * - if approved go to bps approval page
      * - if expired submit info to rtps
      * - if existing do nothing
      * */
      switch (virtualLookup.result) {
        case VirtualLookupResult.Success:
          const updatedPrescreen = getCleanPrescreenResult(virtualLookup);

          this.store.dispatch(RtpsActions.displayPrescreenApprovedOffer(updatedPrescreen,
            customerInfo,
            mockResponse as FetchRtpsMockOptions)
          );
          break;

        case VirtualLookupResult.Expired:
          this.store.dispatch(RtpsActions.brandSubmitRTPS(
            customerInfo,
            mockResponse,
            ),
          );
          break;

        case VirtualLookupResult.ExistingAccount:
          const cleanPrescreen = getCleanPrescreenResult(virtualLookup);

          this.store.dispatch(RtpsActions.displayTenderRebuttal(cleanPrescreen));
          break;
      }
    }),
  );

  /*
  * TODO Add tests
  * */
  offerResponse$ = this.store.actions$.pipe(
    ofType(offerResponse),
    map(action => action.payload),
    tap(offerResponseType => {
      switch (offerResponseType) {
        case OfferResponseType.No:
        case OfferResponseType.NotMe:
        case OfferResponseType.Abandoned:
          this.store.dispatch(OverlayActions.closeOverlay());
      }
    }),
  );

  /*
  * Display pre-screen offer overlay to customer
  * */
  displayPrescreenApprovedOffer$ = this.store.actions$.pipe(
    ofType(displayPrescreenApprovedOffer),
    map(({ payload }) => payload),
    map(({ prescreen, customerInfo, mockResponse }) => {
        const config = this.configUtilService.getConfig();
        const prescreenNum = prescreen.prescreenId as any;
        const mockOptions: RtpsMockOptions = getRtpsMockOptions(mockResponse);
        const data = {
          prescreenId: prescreenNum,
          ...getPreScreenBodyFromCustomerInfo(customerInfo, prescreen),
        };

        return pathForRtps(
          data,
          config.apiKey,
          mockOptions,
        );
      },
    ),
    map(rtpsPathObj => {
      const config = this.configUtilService.getConfig();
      const embeddedUrl =
        `${config.fmcApiBaseUrl}${rtpsPathObj.path}?${rtpsPathObj.queryString}`;

      const rtpsPlacementRequest: GeneratePlacementRequest = {
        placements: [{
          id: '',
          context: {
            LOCATION: 'RTPS-Approval',
            embeddedUrl
          }
        }]
      };
      const adserverClient = container.resolve(EpAdServerClient);
      const placementsResponsePromise = adserverClient.generatePlacements(rtpsPlacementRequest);
      return {embeddedUrl, placementsResponsePromise};
    }),
    switchMap(async ({embeddedUrl, placementsResponsePromise}) => {
      let overlayContent;
      const overlayService = container.resolve(OverlayService);
      try{
        const placementResponse = await placementsResponsePromise;
        if(placementResponse?.placementContent?.length > 0) {
          overlayContent = placementResponse.placementContent[0];
        }
      }catch(err){
        // Do nothing
      }

      /** If there is an error with the adserver or if there is no Approval page set
       * Then display the default prescreen approval page.
       */
      if(!overlayContent){
        overlayContent = {
          id: RTPS_OVERLAY_CONTENT_ID,
          contentType: OverlayContentType.RtpsOverlay,
          contentData: {
            embeddedUrl,
          },
        };
      }

      this.store.dispatch(OverlayActions.showOverlayAuxEvent());
      return of(overlayService.show(overlayContent, undefined, undefined, false));
    }),
  );

  /*
  * Display pre-screen offer overlay to customer
  * */
  displayBpsApprovalOffer$ = this.store.actions$.pipe(
    ofType(RtpsActions.displayBpsApprovalOffer),
    map(({ payload }) => payload),
    map(({ virtualLookup = {}, customerInfo, mockResponse }) => {
        const config = this.configUtilService.getConfig();
        let prescreenIdTmpCast = virtualLookup.prescreenId as any;
        if(!prescreenIdTmpCast && !!customerInfo.prescreenId){
          prescreenIdTmpCast = customerInfo.prescreenId as any;
        }
        const mockOptions: RtpsMockOptions = getRtpsMockOptions(mockResponse);
        const bpsPathObj = pathForBps(
          {
            prescreenId: prescreenIdTmpCast,
            ...getPreScreenBodyFromCustomerInfo(customerInfo, virtualLookup),
          },
          config.apiKey,
          mockOptions,
        );

        return {
          bpsPathObj,
          customerInfo
        };
      },
    ),
    switchMap(({bpsPathObj, customerInfo}) => {
      const config = this.configUtilService.getConfig();
      const showCloseBtn = customerInfo.customerAcceptedOffer || false;
      const embeddedUrl =
        `${config.fmcApiBaseUrl}${bpsPathObj.path}?${bpsPathObj.queryString}`;

      const mockOverlayContent = {
        id: RTPS_OVERLAY_CONTENT_ID,
        contentType: OverlayContentType.RtpsOverlay,
        contentData: {
          embeddedUrl,
        },
      };

      const overlayService = container.resolve(OverlayService);
      this.store.dispatch(OverlayActions.showOverlayAuxEvent());
      /*
      * IMPORTANT!!!!
      * If this event ever sends in more analytics data than now,
      * be sure to notify Famous Footwear of any proposed changes.
      * */
      return of(overlayService.show(mockOverlayContent, undefined, undefined, showCloseBtn));
    }),
  );

  /*
  * Display tender rebuttal overlay to customer
  * */
  displayTenderRebuttal$ = this.store.actions$.pipe(
    ofType(RtpsActions.displayTenderRebuttal),
    map(({ payload}) => payload),
    map((prescreen) => {
      const tenderRebuttalRequest: GeneratePlacementRequest = {
        placements: [{
          id: '',
          context: {
            LOCATION: 'RTPS-Tender-Rebuttal',
            CARD_TYPE: prescreen.cardType || '',
            ENV: this.configUtilService.getConfig().env
          }
        }]
      };
      const adserverClient = container.resolve(EpAdServerClient);
      const placementsResponse = adserverClient.generatePlacements(tenderRebuttalRequest);
      return placementsResponse;
    }),
    switchMap(async placementsResponsePromise => {
      const placementsResponse = await placementsResponsePromise;
      if(!!placementsResponse?.placementContent.length) {
        const overlayContent = placementsResponse?.placementContent[0];
        const overlayService = container.resolve(OverlayService);
        this.store.dispatch(OverlayActions.showOverlayAuxEvent());
        this.publicEventEmitter.emitter.emit('FMC:TENDER_REBUTTAL_DISPLAYED');
        return of(overlayService.show(overlayContent, undefined, undefined, true));
      }
    }),
  );


  constructor(private store?: EpStore,
              private publicEventEmitter?: PublicEventEmitterService,
              private analytics?: Analytics,
              private configUtilService?: ConfigUtilService,
  ) {
  }

  bindSideEffects() {
    return merge(
      this.brandSubmitRTPS$,
      this.submitRtpsSuccess$,
      this.displayPrescreenApprovedOffer$,
      this.offerResponse$,
      this.fetchPrescreen$,
      this.submitVirtualLookupSuccess$,
      this.virtualLookupReq$,
      this.displayBpsApprovalOffer$,
      this.displayTenderRebuttal$
    ).subscribe();
  }
}
